import { z } from "zod";
import { MEMBER_LIFECYCLE_STAGES, memberLifecycleStageSchema, } from "./members.js";
/**
 * GET /api/communities/:id/lifecycle — TASK-033 wire contract.
 *
 * The endpoint returns the last 30 days of LifecycleSnapshot rows for a
 * community, backfilled to a fixed-length 30-entry trend even when the
 * community has only a few days of snapshot data (mirrors TASK-028's
 * health trend zero-fill convention so the Recharts x-axis is always
 * continuous).
 *
 * The lifecycle distribution is the §6.3.2 "stacked area chart" data:
 * for each UTC day, the count of Members in each of the six stages
 * (NEW / ACTIVE / ENGAGED / DORMANT / AT_RISK / CHURNED). The Mini App
 * "Lifecycle" page renders this as a stacked area chart so the OWNER can
 * see the cohort composition over time — NOT as a pie chart, because per
 * §6.3.2 "time dimension matters" (a single-day snapshot hides the
 * trends that signal community health drift).
 *
 * INTERPRETATION GUIDANCE: per §6.3.2, AT_RISK 30-50% is normal in large
 * public communities — mass lurking is the norm, NOT a defect. The Mini
 * App tooltip surfaces this so a fresh owner doesn't panic. Document
 * here on the contract too so downstream consumers (PDF report,
 * embedded views) stay aligned.
 *
 * AVAILABILITY: empty trend (zero snapshots) is a valid response —
 * `dataAvailable=false` so the Mini App can render an onboarding empty
 * state ("Collecting lifecycle data") instead of a flat-zero stacked
 * chart. `daysWithData` reports how many of the 30 trend slots actually
 * carry a snapshot — useful for the days-remaining counter.
 */
/**
 * Fixed length of the trend window. 30 days mirrors the health trend.
 * Pinned as a constant so the Zod `.length(N)` constraint and the
 * backend projector's window builder share one source of truth.
 */
export const COMMUNITY_LIFECYCLE_TREND_DAYS = 30;
/**
 * Per-stage counts for one calendar day. Always fully populated — every
 * stage in `MEMBER_LIFECYCLE_STAGES` carries a non-negative integer,
 * even on days with zero classified members (the projector zero-fills).
 *
 * The shape mirrors `MemberStageCounts` (TASK-031) but is duplicated
 * here so the lifecycle endpoint can evolve independently if a future
 * stage set diverges from the listing endpoint.
 */
export const lifecycleStageCountsSchema = z.object({
    NEW: z.number().int().min(0),
    ACTIVE: z.number().int().min(0),
    ENGAGED: z.number().int().min(0),
    DORMANT: z.number().int().min(0),
    AT_RISK: z.number().int().min(0),
    CHURNED: z.number().int().min(0),
});
/**
 * One day in the 30-day trend. `date` is a UTC YYYY-MM-DD calendar day —
 * same format as the health and overview trends. `totalMembers` is the
 * sum of the six stage counts; the projector recomputes it server-side
 * from the JSONB `counts` for cheap UI rendering.
 */
export const lifecycleDailyPointSchema = z.object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    counts: lifecycleStageCountsSchema,
    totalMembers: z.number().int().min(0),
});
/**
 * Wire response. The lifecycle endpoint is NOT plan-gated for MVP — even
 * FREE owners get the full trend, because it's the primary signal of
 * community health drift and the gating is already on the Health
 * breakdown (TASK-028). If a future tier change introduces gating, add
 * a `truncated` discriminator like TASK-028 did.
 */
export const lifecycleResponseSchema = z.object({
    trend: z
        .array(lifecycleDailyPointSchema)
        .length(COMMUNITY_LIFECYCLE_TREND_DAYS),
    dataAvailable: z.boolean(),
    /**
     * Number of trend slots that carry a real snapshot (vs. zero-filled).
     * UI uses this to render the "X / 30 days collected" subtitle on the
     * onboarding empty state.
     */
    daysWithData: z
        .number()
        .int()
        .min(0)
        .max(COMMUNITY_LIFECYCLE_TREND_DAYS),
    generatedAt: z.string().datetime(),
});
/**
 * Helper exported for the projector + tests: a zero-filled
 * LifecycleStageCounts. Re-uses MEMBER_LIFECYCLE_STAGES iteration order
 * so a future stage addition flags one place to touch (this constant)
 * rather than a scatter of literal `{ NEW: 0, ACTIVE: 0, ... }` objects.
 */
export function zeroLifecycleStageCounts() {
    const out = {};
    for (const stage of MEMBER_LIFECYCLE_STAGES) {
        out[stage] = 0;
    }
    return out;
}
/**
 * Re-export the stage schema so consumers can build their own
 * cross-cutting validators without two imports.
 */
export { memberLifecycleStageSchema };
//# sourceMappingURL=lifecycle.js.map